import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  AlertTriangle,
  RotateCcw,
  Save,
  Clock,
  ImageOff,
} from "lucide-react";
import { useSignedImage } from "../../hooks/useSignedImage";

import { Button } from "@/ui/components/ui/button";
import { Switch } from "@/ui/components/ui/switch";
import { cn } from "@/ui/lib/utils";
import { formatDateTime, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { motion, AnimatePresence } from "motion/react";

import BinaryFamilyControl from "./binary-family-control";
import PipelineGroupSection from "./pipeline-group-section";
import LabelTierBadge from "./label-tier-badge";
import {
  useGetReviewDetail,
  useReviewClassification,
  useGetClassificationTaxonomy,
  useGetEventDetail,
} from "../../api";
import { eventStateLabel } from "../../utils/event-colors";
import { getReviewDetail } from "../../api/requests/review.requests";
import { QUERY_KEYS } from "../../api/config";
import {
  buildCurrentFamilyState,
  memberToCanonical,
  splitLabelsByTaxonomy,
} from "../../utils/taxonomy";
import {
  buildPipelineGroups,
  computeQualityGate,
  familyForShortcut,
  FAMILY_SHORTCUTS,
  getMemberDisplayOrder,
  groupedShortcutHints,
  nextCycleSelection,
} from "../../utils/pipeline-groups";
import type { ReviewCommand } from "../../types/review";
import type { FamilyMemberSelection } from "../../types/taxonomy";

/** Footer hint shorthands are internal tokens, mapped to display text here. */
const SHORTCUT_GROUP_KEYS: Record<
  string,
  "pipelineGroups.short.core" | "pipelineGroups.short.gate" | "pipelineGroups.short.env"
> = {
  core: "pipelineGroups.short.core",
  gate: "pipelineGroups.short.gate",
  env: "pipelineGroups.short.env",
};

/* ──────────────────────────────────────────────────────────────────
 * ReviewCarousel — guided walkthrough mode.
 *
 * Keyboard (designed for speed — clear 200 images in ~15 min):
 *
 *   B C G       Cycle core families · 1 2 3 gate · N R D M env
 *               (members[] → none — "none" unstages)
 *   ← / →       prev / next image
 *   Enter / S   save & advance
 *   Space       skip (next without saving)
 *   Q           reset staged
 *   V           toggle "mark frame as invalid"
 *   Esc         exit
 * ────────────────────────────────────────────────────────────────── */

interface ReviewCarouselProps {
  imageIds: number[];
  onClose: () => void;
}

const ReviewCarousel = ({ imageIds, onClose }: ReviewCarouselProps) => {
  const { t } = useTranslation("mlExports");
  const queryClient = useQueryClient();
  const { data: taxonomy } = useGetClassificationTaxonomy();
  const reviewMutation = useReviewClassification();

  const [index, setIndex] = useState(0);
  const currentImageId = imageIds[index];

  const { data: detail, isLoading: detailLoading } =
    useGetReviewDetail(currentImageId);

  const { src: imageSrc, onError: handleImageError, hasFailed } = useSignedImage({
    imageId: currentImageId,
    initialUrl: detail?.signed_url ?? "",
  });

  const { data: eventDetail } = useGetEventDetail(detail?.event_uuid);
  const eventCore = eventDetail?.event ?? null;
  const eventLabel = eventCore ? eventStateLabel(eventCore.state, t) : null;

  // ─── Staged state ────────────────────────────────────────────────
  const [staged, setStaged] = useState<Partial<Record<string, number>>>({});
  const [dumpValidStaged, setDumpValidStaged] = useState<boolean | null>(null);

  useEffect(() => {
    setStaged({});
    setDumpValidStaged(null);
  }, [currentImageId]);

  // Prefetch neighbors
  useEffect(() => {
    const neighbors = [imageIds[index + 1], imageIds[index - 1]].filter(
      (id): id is number => typeof id === "number",
    );
    for (const nid of neighbors) {
      queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.REVIEW_DETAIL, nid],
        queryFn: () => getReviewDetail(nid),
        staleTime: 60_000,
      });
    }
  }, [index, imageIds, queryClient]);

  // ─── Derived ─────────────────────────────────────────────────────
  const currentState = useMemo(
    () =>
      detail
        ? buildCurrentFamilyState(detail.labels ?? [], taxonomy)
        : new Map(),
    [detail, taxonomy],
  );

  const { otherLabels } = useMemo(
    () => splitLabelsByTaxonomy(detail?.labels ?? [], taxonomy),
    [detail, taxonomy],
  );

  // ─── Pipeline groups + live quality gate ─────────────────────────
  const pipelineGroups = useMemo(() => buildPipelineGroups(taxonomy), [taxonomy]);

  const qualityGate = useMemo(() => {
    const gateGroup = pipelineGroups.find((g) => g.key === "quality_gate");
    if (!gateGroup || gateGroup.families.length === 0) return null;
    return computeQualityGate(gateGroup.families, currentState, staged);
  }, [pipelineGroups, currentState, staged]);

  const stagedCount =
    Object.keys(staged).length + (dumpValidStaged === null ? 0 : 1);

  const effectiveDumpValid =
    dumpValidStaged !== null
      ? dumpValidStaged
      : detail?.dump_image_valid ?? true;

  // ─── Navigation ──────────────────────────────────────────────────
  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= imageIds.length) return;
      setIndex(next);
    },
    [imageIds.length],
  );
  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  // ─── Staging ─────────────────────────────────────────────────────
  const handleStage = (familyKey: string, selection: FamilyMemberSelection) => {
    setStaged((prev) => {
      const entry = currentState.get(familyKey);
      // "none", re-picking the saved member, or re-clicking a staged
      // creation all unstage — there is nothing to save for this family.
      const isOriginalMember = entry && entry.memberIndex === selection;
      const isClickingStagedMember = !entry && prev[familyKey] === selection;
      if (selection === "none" || isOriginalMember || isClickingStagedMember) {
        const rest = { ...prev };
        delete rest[familyKey];
        return rest;
      }
      return { ...prev, [familyKey]: selection };
    });
  };

  /** Cycle a family: members[] → none → members[0]. */
  const handleCycle = (familyKey: string) => {
    const family = taxonomy?.families.find((f) => f.key === familyKey);
    if (!family || family.members.length === 0) return;
    const entry = currentState.get(familyKey);
    // Corrected rows are locked — undo the correction first.
    if (entry?.label.review_state === "MODEL_CORRECTED") return;
    const current = entry?.memberIndex ?? null;
    const effective = staged[familyKey] ?? current ?? "none";
    let next = nextCycleSelection(family.members.length, effective);
    // "none" on an unstaged row ≡ the saved label — skip it so the
    // cycle keeps moving when the saved member is the last one.
    if (next === "none" && current !== null && staged[familyKey] === undefined) {
      next = nextCycleSelection(family.members.length, next);
    }
    handleStage(familyKey, next);
  };

  const handleReset = () => {
    setStaged({});
    setDumpValidStaged(null);
  };

  const handleToggleValid = () =>
    setDumpValidStaged((prev) =>
      prev === null ? !(detail?.dump_image_valid ?? true) : !prev,
    );

  // ─── Save & advance ─────────────────────────────────────────────
  const handleSaveAndAdvance = useCallback(async () => {
    if (!taxonomy || !detail) return;
    if (stagedCount === 0) {
      goNext();
      return;
    }
    const commands: ReviewCommand[] = [];
    for (const [familyKey, memberIndex] of Object.entries(staged)) {
      if (memberIndex === undefined) continue;
      const family = taxonomy.families.find((f) => f.key === familyKey);
      if (!family) continue;
      const canonical = memberToCanonical(family, memberIndex);
      if (!canonical) continue;
      const entry = currentState.get(familyKey);
      if (!entry) {
        // Add mode — no model prediction, create a fresh human label.
        commands.push({ type: "create", human_label: canonical });
      } else if (entry.label.origin === "human") {
        // Changing a human-added label = delete + create (update would
        // fail — origin="human" rows are not corrections).
        commands.push({ type: "delete", id: entry.label.id });
        commands.push({ type: "create", human_label: canonical });
      } else {
        commands.push({
          type: "update",
          id: entry.label.id,
          human_label: canonical,
        });
      }
    }
    const payload = {
      commands,
      ...(dumpValidStaged !== null
        ? { dump_image_valid: dumpValidStaged }
        : {}),
    };

    try {
      await reviewMutation.mutateAsync({ imageId: detail.image_id, payload });
      toast.success(
        commands.length > 0
          ? t("toast.saved", {
              count: commands.length,
              value: formatNumber(commands.length),
            })
          : t("toast.savedPlain"),
      );
      if (index < imageIds.length - 1) goNext();
    } catch {
      toast.error(t("toast.saveFailed"));
    }
  }, [
    t,
    taxonomy,
    detail,
    stagedCount,
    staged,
    currentState,
    dumpValidStaged,
    reviewMutation,
    goNext,
    index,
    imageIds.length,
  ]);

  // ─── Keyboard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;

      const k = e.key.toLowerCase();

      // Pipeline shortcuts: B C G core · 1 2 3 gate · N R D M env
      if (taxonomy) {
        const cycled = familyForShortcut(taxonomy, k);
        if (cycled) {
          e.preventDefault();
          handleCycle(cycled.key);
          return;
        }
      }

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goPrev();
          break;
        case "ArrowRight":
          e.preventDefault();
          goNext();
          break;
        case " ":
          e.preventDefault();
          goNext();
          break;
        case "Enter":
          e.preventDefault();
          handleSaveAndAdvance();
          break;
        case "s":
        case "S":
          e.preventDefault();
          handleSaveAndAdvance();
          break;
        case "q":
        case "Q":
          e.preventDefault();
          handleReset();
          break;
        case "v":
        case "V":
          e.preventDefault();
          handleToggleValid();
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomy, currentState, staged, goPrev, goNext, handleSaveAndAdvance, onClose]);

  if (imageIds.length === 0) return null;

  const progress = ((index + 1) / imageIds.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "hsl(240 10% 4% / 0.97)" }}
    >
      {/* Accent progress bar */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/5">
        <motion.div
          className="h-full bg-white"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
            title={t("detail.header.close")}
          >
            <X className="h-5 w-5" />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">
                {t("carousel.position", { index: index + 1 })}
                <span className="text-zinc-500">/{imageIds.length}</span>
              </span>
              {eventCore && eventLabel && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide text-white"
                  style={{
                    backgroundColor: eventCore.color,
                    boxShadow: `0 0 8px ${eventCore.color}40`,
                  }}
                >
                  {eventLabel}
                </span>
              )}
            </div>
            {detail && (
              <div className="text-[11px] text-zinc-500 font-mono">
                {detail.machine_name} ·{" "}
                {detail.total_frames != null
                  ? t("carousel.frameOfTotal", {
                      frame: detail.event_frame,
                      total: detail.total_frames,
                    })
                  : t("detail.meta.frame", { frame: detail.event_frame })}{" "}
                · {formatDateTime(new Date(detail.timestamp))}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Keyboard legend */}
          <div className="hidden lg:flex items-center gap-2 text-[10px] font-mono text-zinc-600 mr-2 flex-wrap">
            {taxonomy && groupedShortcutHints().map((group) => (
              <span key={group.label} className="inline-flex items-center gap-0.5">
                {group.keys.map((key) => (
                  <kbd
                    key={key}
                    className="inline-flex items-center justify-center h-4 min-w-4 px-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700 text-[9px] font-semibold uppercase"
                  >
                    {key}
                  </kbd>
                ))}
                <span className="text-zinc-500">
                  {t(SHORTCUT_GROUP_KEYS[group.label] ?? "pipelineGroups.short.core")}
                </span>
              </span>
            ))}
            <span className="inline-flex items-center gap-0.5 ml-1">
              <kbd className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                ←→
              </kbd>
              <span className="text-zinc-500">{t("carousel.nav")}</span>
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={stagedCount === 0}
            className="text-xs h-8 text-zinc-400 hover:text-white hover:bg-white/10"
          >
            {t("detail.footer.reset")}
            <kbd className="ml-1 text-[9px] opacity-50">Q</kbd>
          </Button>

          <Button
            size="sm"
            onClick={handleSaveAndAdvance}
            disabled={reviewMutation.isPending}
            className={cn(
              "h-8",
              stagedCount > 0 &&
                "bg-amber-500 hover:bg-amber-600 text-white",
            )}
          >
            {reviewMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            {stagedCount > 0 ? t("carousel.saveAndNext") : t("carousel.next")}
            <kbd className="ml-1.5 text-[9px] opacity-50">S</kbd>
          </Button>
        </div>
      </div>

      {/* ─── Main body ──────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Prev arrow */}
        <button
          onClick={goPrev}
          disabled={index === 0}
          className={cn(
            "w-14 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/5 transition-colors",
            index === 0 && "opacity-20 cursor-not-allowed",
          )}
          title={t("carousel.previous")}
        >
          <ChevronLeft className="h-8 w-8" />
        </button>

        {/* Image + controls */}
        <div className="flex-1 flex items-center justify-center p-4 min-w-0">
          {detailLoading || !detail ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            >
              <Loader2 className="h-8 w-8 text-zinc-600" />
            </motion.div>
          ) : (
            <div className="flex gap-6 h-full items-center max-w-[1400px] w-full">
              {/* Image */}
              <div className="flex-1 min-w-0 h-full flex items-center justify-center relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={detail.image_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="relative"
                  >
                    {hasFailed ? (
                      <div className="max-h-[calc(100vh-160px)] aspect-video flex items-center justify-center bg-zinc-900 rounded-xl">
                        <ImageOff className="h-16 w-16 text-zinc-600" />
                      </div>
                    ) : (
                      <img
                        src={imageSrc}
                        alt={t("detail.imageAlt", { id: detail.image_id })}
                        className="max-h-[calc(100vh-160px)] max-w-full object-contain rounded-xl"
                        style={{ boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}
                        onError={handleImageError}
                      />
                    )}
                    {/* Invalid overlay */}
                    {!effectiveDumpValid && (
                      <div className="absolute inset-0 bg-yellow-900/30 border-2 border-dashed border-yellow-500/50 rounded-xl flex items-center justify-center">
                        <span className="px-3 py-1.5 rounded-lg bg-yellow-600/90 text-white text-xs font-bold uppercase tracking-wider">
                          {t("carousel.invalidFrame")}
                        </span>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right-side controls */}
              <div className="w-[360px] shrink-0 overflow-y-auto max-h-full space-y-4 pr-2">
                {/* Validity toggle */}
                <div className="rounded-lg border border-white/5 bg-white/[0.03] p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <AlertTriangle
                      className={cn(
                        "h-4 w-4 shrink-0",
                        !effectiveDumpValid
                          ? "text-yellow-500"
                          : "text-zinc-600",
                      )}
                    />
                    <div>
                      <div className="text-xs font-medium text-zinc-300">
                        {t("validity.title")}
                      </div>
                      <div className="text-[10px] text-zinc-600">
                        <Trans
                          t={t}
                          i18nKey="validity.hintKey"
                          components={{
                            key: (
                              <kbd className="px-1 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-[9px] font-mono text-zinc-400" />
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={!effectiveDumpValid}
                    onCheckedChange={handleToggleValid}
                  />
                </div>

                {/* Binary families */}
                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
                    {t("classification.title")}
                  </h3>
                  {!taxonomy ? (
                    <Loader2 className="h-4 w-4 animate-spin text-zinc-600" />
                  ) : (
                    <div className="space-y-4">
                      {pipelineGroups.map((group) => (
                        <PipelineGroupSection
                          key={group.key}
                          group={group}
                          gate={
                            group.key === "quality_gate" ? qualityGate : undefined
                          }
                          variant="overlay"
                        >
                          {group.families.map((family) => {
                            const entry = currentState.get(family.key);
                            const currentIndex = entry?.memberIndex ?? null;
                            const reviewState = entry?.label.review_state;
                            const stagedIndex = staged[family.key];
                            const selectedIndex: FamilyMemberSelection =
                              stagedIndex !== undefined
                                ? stagedIndex
                                : currentIndex ?? "none";

                            const isCorrected =
                              reviewState === "MODEL_CORRECTED";
                            const isHumanAdded = reviewState === "HUMAN_ADDED";
                            const isOutdated =
                              reviewState === "REVIEW_OUTDATED";
                            const isStaged = stagedIndex !== undefined;

                            const conf =
                              entry?.label.confidence != null
                                ? t("caption.confidence", {
                                    value: formatNumber(
                                      Math.round(entry.label.confidence * 100),
                                    ),
                                  })
                                : "";

                            const captionParts: React.ReactNode[] = [];
                            if (entry) {
                              captionParts.push(
                                <span key="origin" className="text-zinc-500">
                                  {isHumanAdded
                                    ? t("caption.addedManually")
                                    : isOutdated
                                      ? t("caption.outdated", { conf })
                                      : isCorrected
                                        ? t("caption.corrected", { conf })
                                        : t("caption.model", { conf })}
                                </span>,
                              );
                              captionParts.push(
                                <LabelTierBadge
                                  key="tier"
                                  label={entry.label}
                                  alert={detail?.alert}
                                  className="ml-1.5 align-middle"
                                />,
                              );
                              if (isStaged) {
                                captionParts.push(
                                  <span
                                    key="arrow"
                                    className="text-amber-400 font-medium"
                                  >
                                    {t("caption.staged")}
                                  </span>,
                                );
                              }
                            } else {
                              captionParts.push(
                                <span key="none" className="italic text-zinc-600">
                                  {isStaged ? (
                                    <span className="text-violet-400 font-medium not-italic">
                                      {t("caption.willAdd")}
                                    </span>
                                  ) : (
                                    t("caption.noPrediction")
                                  )}
                                </span>,
                              );
                            }

                            return (
                              <BinaryFamilyControl
                                key={family.key}
                                family={family}
                                currentIndex={currentIndex}
                                selectedIndex={selectedIndex}
                                disabled={isCorrected}
                                staged={isStaged}
                                corrected={isCorrected}
                                reviewOutdated={isOutdated}
                                humanAdded={isHumanAdded}
                                displayOrder={getMemberDisplayOrder(family)}
                                gateDot={
                                  group.key === "quality_gate"
                                    ? qualityGate?.families[family.key]
                                    : undefined
                                }
                                shortcutKey={FAMILY_SHORTCUTS[family.key]}
                                caption={<>{captionParts}</>}
                                onChange={(selection) =>
                                  handleStage(family.key, selection)
                                }
                              />
                            );
                          })}
                        </PipelineGroupSection>
                      ))}
                    </div>
                  )}
                </div>

                {/* Other labels */}
                {otherLabels.length > 0 && taxonomy && (
                  <div>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                      {t("otherLabels.title")}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {otherLabels.map((label) => {
                        const meta = taxonomy.other_labels.find(
                          (o) => o.canonical === label.final_label,
                        );
                        const isLegacy = meta?.category === "legacy";
                        return (
                          <div
                            key={label.id}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border",
                              isLegacy
                                ? "border-dashed border-amber-500/30 text-amber-400/80"
                                : "border-zinc-700 text-zinc-500",
                            )}
                          >
                            {isLegacy ? (
                              <Clock className="h-2.5 w-2.5" />
                            ) : (
                              <Lock className="h-2.5 w-2.5" />
                            )}
                            {meta?.display_name ?? label.final_label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Staged changes indicator */}
                {stagedCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1.5 text-xs text-amber-400"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t("carousel.unsavedChanges", {
                      count: stagedCount,
                      value: formatNumber(stagedCount),
                    })}
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Next arrow */}
        <button
          onClick={goNext}
          disabled={index >= imageIds.length - 1}
          className={cn(
            "w-14 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/5 transition-colors",
            index >= imageIds.length - 1 && "opacity-20 cursor-not-allowed",
          )}
          title={t("carousel.nextArrow")}
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      </div>
    </motion.div>
  );
};

export default ReviewCarousel;
